import { z } from 'zod';
import { env } from '../../env';
import { getCurrentOrgId } from '@shared/data/use-current-org';
import { ApiError, type ApiErrorBody } from './errors';
import { queryClient } from './query-client';

/**
 * apiClient - the single fetch path for the app.
 *
 * Ported from v1's `authenticatedFetch` (src/api/api.ts):
 *   - Auto-attaches Bearer access token from localStorage
 *   - On 401: calls POST /auth/refresh with refreshToken, retries the original
 *     request once on success; on failure clears tokens, emits an 'auth:expired'
 *     event (the auth provider listens and flips status), and hard-redirects to
 *     /auth as a backstop so an in-flight query that 401s can't trap the user in
 *     a 401 loop with orgId=unknown (#61).
 *
 * Callers MUST go through this. Raw `fetch` from feature code is banned (see
 * eslint.config.js).
 */

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const AUTH_ROUTE = '/auth';

/**
 * Refresh response, zod-parsed at this boundary. The conmap-auto backend wraps
 * the tokens under `data` (`{ success, data: { accessToken, refreshToken } }`,
 * see @shared/auth/auth-provider). A bare top-level shape is also accepted so a
 * mock/legacy backend doesn't silently fail the refresh. The previous code read
 * `data.accessToken` off the wrapped body - always `undefined` - so EVERY 401
 * cleared tokens even with a valid refresh token, which is the #61 loop.
 */
const RefreshTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1).optional(),
});
const RefreshResponseSchema = z.union([
  z.object({ data: RefreshTokensSchema }).transform((r) => r.data),
  RefreshTokensSchema,
]);

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON body - stringified automatically; sets Content-Type. */
  json?: unknown;
  /** Raw body (FormData, Blob, string). Mutually exclusive with `json`. */
  body?: BodyInit;
  /** Skip auth header (used for /auth/login, /auth/refresh, etc.). */
  auth?: false;
  /** Query params appended to the URL. */
  params?: Record<string, string | number | boolean | undefined | null>;
  /** AbortSignal forwarded to fetch - TanStack Query passes one in. */
  signal?: AbortSignal;
}

function buildUrl(path: string, params?: RequestOptions['params']): string {
  const base = env.BACKEND_URL.replace(/\/$/, '');
  const rel = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${rel}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

async function parseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') ?? '';
  if (res.status === 204) return null;
  if (ct.includes('application/json')) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  // An empty 200 body (e.g. a controller returning `null`) reads as "" via
  // text(); treat it as null so callers get "no data", not an unparseable "".
  return (await res.text()) || null;
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Refresh the access token, at most once at a time.
 *
 * Exported because the auth provider used to carry its own two copies of this
 * logic. Rotation makes duplicates actively harmful rather than merely
 * redundant: two independent refreshes race, the first rotates and revokes the
 * prior session, the second presents the token it had already read and the
 * backend answers "Session revoked" -- so a user with a perfectly good session
 * gets logged out. Reproduced live: refresh succeeded at 2:55:24, a second one
 * failed three seconds later, and the client wiped both tokens.
 *
 * Every caller must come through here so the in-flight promise is actually
 * shared.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;

  refreshInFlight = (async () => {
    try {
      const res = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const parsed = RefreshResponseSchema.safeParse(await res.json());
      if (!parsed.success) return null;
      const { accessToken, refreshToken: nextRefresh } = parsed.data;
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      if (nextRefresh) {
        localStorage.setItem(REFRESH_TOKEN_KEY, nextRefresh);
      }
      // Recovered: a later genuine 401 should be allowed to clear + redirect.
      sessionExpired = false;
      return accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Session-clear is idempotent within a single expiry: many in-flight queries
 * can each 401 at once, but we clear + dispatch + redirect only once until a
 * fresh sign-in resets it. Without this guard a screenful of failing queries
 * would each fire `auth:expired` and re-trigger the redirect - the #61 loop.
 */
let sessionExpired = false;

function isOnAuthRoute(): boolean {
  if (typeof window === 'undefined') return true;
  const path = window.location.pathname;
  return path === AUTH_ROUTE || path.startsWith(`${AUTH_ROUTE}/`);
}

function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // Session-expiry parity with AuthProvider.signOut (#320): the terminal 401
  // path must ALSO purge the query cache and the identity-bearing keys, so a
  // re-login can't be served stale role-bearing data or inherit the prior
  // user's identity. clearTokens hard-redirects to /auth (full reload) below,
  // which resets in-memory state anyway, but clear explicitly for correctness
  // in case that redirect ever becomes a navigate(). query-client lives next to
  // this module (react-query + errors only), so the import is cycle-safe. These
  // run before the one-shot guard so they always fire on a terminal 401.
  queryClient.clear();
  localStorage.removeItem('armoriq:current-email');
  localStorage.removeItem('armoriq:org-memberships:v1');
  if (sessionExpired) return;
  sessionExpired = true;
  // Auth provider listens for this to flip status -> 'unauthenticated', which
  // makes <RequireAuth> redirect. We also hard-redirect here as a backstop:
  // RequireAuth only re-renders for routes mounted under it, and an in-flight
  // query can keep 401-ing before that state propagates.
  window.dispatchEvent(new CustomEvent('auth:expired'));
  // Never redirect while already on an /auth route - that's the redirect loop.
  if (typeof window !== 'undefined' && !isOnAuthRoute()) {
    window.location.assign(AUTH_ROUTE);
  }
}

/**
 * Re-arm the one-shot expiry guard. Call this on a fresh sign-in so that a
 * later session expiry can clear + redirect again. The auth provider calls it
 * after a successful login (the login flow doesn't go through this module).
 */
export function resetSessionExpired(): void {
  sessionExpired = false;
}

/** Test-only alias - the guard is module-global and leaks across specs. */
export const __resetSessionExpiredForTests = resetSessionExpired;

/**
 * Seconds until retry is allowed, read off a 429 response.
 *
 * conmap does not send a bare `Retry-After`. Its throttler emits per-group
 * suffixed headers instead - `Retry-After-auth: 41`,
 * `X-RateLimit-Reset-delegation: 40` - so we scan for both the standard name
 * and any suffixed variant and take the soonest positive value. Returns null
 * when the server told us nothing; callers must not invent a countdown.
 */
function readRetryAfterSeconds(res: Response): number | null {
  const candidates: number[] = [];
  res.headers.forEach((value, name) => {
    const lower = name.toLowerCase();
    if (!lower.startsWith('retry-after') && !lower.startsWith('x-ratelimit-reset')) return;
    const seconds = Number.parseInt(value, 10);
    if (Number.isFinite(seconds) && seconds > 0) candidates.push(seconds);
  });
  return candidates.length > 0 ? Math.min(...candidates) : null;
}

async function doFetch(url: string, init: RequestInit, withAuth: boolean): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (withAuth) {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  // Attach the currently-selected org so the backend scopes data to the right
  // tenant (resolveCallerOrgId reads this header). Without it the backend falls
  // back to the user's primary org, which is wrong when the user has switched.
  const orgId = getCurrentOrgId();
  if (orgId) headers.set('x-organization-id', orgId);
  return fetch(url, { ...init, headers });
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, body, auth, params, signal, ...rest } = options;
  const withAuth = auth !== false;
  const url = buildUrl(path, params);

  const init: RequestInit = { ...rest, signal };
  if (json !== undefined) {
    init.body = JSON.stringify(json);
    init.headers = { ...rest.headers, 'Content-Type': 'application/json' };
  } else if (body !== undefined) {
    init.body = body;
  }

  let res: Response;
  try {
    res = await doFetch(url, init, withAuth);
  } catch (err) {
    throw new ApiError({
      kind: 'network',
      status: 0,
      url,
      body: null,
      message: err instanceof Error ? err.message : 'Network request failed',
    });
  }

  // 401 + auth-required → refresh and retry once
  if (res.status === 401 && withAuth) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await doFetch(url, init, true);
    } else {
      clearTokens();
    }
  }

  if (!res.ok) {
    const parsedBody = (await parseBody(res)) as ApiErrorBody | string | null;
    const errBody: ApiErrorBody | null =
      typeof parsedBody === 'string' ? { message: parsedBody } : (parsedBody ?? null);
    throw ApiError.fromStatus(res.status, url, errBody, readRetryAfterSeconds(res));
  }

  const parsed = await parseBody(res);
  return parsed as T;
}

export const apiClient = {
  get: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T = unknown>(path: string, json?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', json }),
  put: <T = unknown>(path: string, json?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', json }),
  patch: <T = unknown>(path: string, json?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', json }),
  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

export type ApiClient = typeof apiClient;
